import { useMutation } from "@apollo/client";
import { useMemo } from "react";
import { z } from "zod";
import { type FaEligibilityOutcome, FfgaIncomeBand } from "@medbillai/generated-graphql-types";
import { Body3, FieldError, SelectionGroup, YStack } from "@medbillai/ui";
import { ffgaIncomeBandsFor, formatIncomeBand, isFfgaIncomeBandKey } from "@medbillai/schemas";
import { OnboardingScreen } from "../../components/OnboardingScreen";
import { type OnDone, tagged } from "../../lib/flow-engine";
import { useFormEngine, visibleFieldError } from "../../lib/form-engine";
import { checkFaEligibilityMutation } from "./utils/queries";
export type IncomeOutcomes = {
  checked: {
    outcome: FaEligibilityOutcome;
  };
  back: {};
};
const Outcome = tagged<IncomeOutcomes>();
const HELPER_TEXT = "Include your spouse's income if you file taxes jointly.";
const BAND_REQUIRED_MESSAGE = "Pick a range";
const incomeSchema = z.object({
  incomeBand: z.string({
    required_error: BAND_REQUIRED_MESSAGE
  }).refine(isFfgaIncomeBandKey, BAND_REQUIRED_MESSAGE)
});
type IncomeFormDraft = Partial<z.input<typeof incomeSchema>>;
export function Income({
  householdSize,
  onDone
}: {
  householdSize: number;
  onDone: OnDone<IncomeOutcomes>;
}) {
  const [checkFaEligibility] = useMutation(checkFaEligibilityMutation);
  const bands = useMemo(() => ffgaIncomeBandsFor(householdSize), [householdSize]);
  const {
    form,
    formError,
    submit
  } = useFormEngine<IncomeFormDraft, z.output<typeof incomeSchema>, FaEligibilityOutcome>({
    defaultValues: {
      incomeBand: undefined
    },
    schema: incomeSchema,
    onSubmit: async values => {
      // Throws bubble to the engine: Sentry capture + the generic form error.
      const {
        data
      } = await checkFaEligibility({
        variables: {
          input: {
            householdSize,
            incomeBand: FfgaIncomeBand[values.incomeBand]
          }
        }
      });
      const outcome = data?.checkFaEligibility.outcome;
      if (!outcome) {
        throw new Error("checkFaEligibility returned no outcome");
      }
      return {
        status: "ok",
        data: outcome
      };
    },
    onSubmitSuccess: (_values, {
      data: outcome
    }) => {
      onDone(Outcome.checked({
        outcome
      }));
    }
  });
  return <OnboardingScreen data-sentry-element="OnboardingScreen" data-sentry-component="Income" data-sentry-source-file="Income.tsx">
      <OnboardingScreen.Nav onBackPress={() => onDone(Outcome.back())} showLogo={false} data-sentry-element="OnboardingScreen.Nav" data-sentry-source-file="Income.tsx" />
      <OnboardingScreen.Content data-sentry-element="OnboardingScreen.Content" data-sentry-source-file="Income.tsx">
        <OnboardingScreen.Header alignment="left" title="What's your household annual income?" subtitle="Pick the range for your household's pre-tax income." data-sentry-element="OnboardingScreen.Header" data-sentry-source-file="Income.tsx" />
        <YStack gap="$sp2" data-sentry-element="YStack" data-sentry-source-file="Income.tsx">
          <form.Field name="incomeBand" data-sentry-element="form.Field" data-sentry-source-file="Income.tsx">
            {field => {
            const errorMessage = visibleFieldError(field.state.meta);
            return <YStack gap="$sp1.5">
                  <SelectionGroup mode="radio" required label="Annual household income" value={field.state.value ?? ""} onChange={value => {
                field.handleChange(value);
              }}>
                    {bands.map(band => <SelectionGroup.Row prominent key={band.key} testID={`income.band.${band.key}`} value={band.key} label={formatIncomeBand(band)} />)}
                  </SelectionGroup>
                  {errorMessage ? null : <Body3 color="$textSubtle">{HELPER_TEXT}</Body3>}
                  <FieldError message={errorMessage} />
                </YStack>;
          }}
          </form.Field>
        </YStack>
      </OnboardingScreen.Content>
      <OnboardingScreen.Footer data-sentry-element="OnboardingScreen.Footer" data-sentry-source-file="Income.tsx">
        <OnboardingScreen.FormError message={formError} data-sentry-element="OnboardingScreen.FormError" data-sentry-source-file="Income.tsx" />
        <form.Subscribe selector={state => state.isSubmitting} data-sentry-element="form.Subscribe" data-sentry-source-file="Income.tsx">
          {isSubmitting => <OnboardingScreen.PrimaryAction testID="income.check-eligibility" variant="brand" disabled={isSubmitting} loading={isSubmitting}
        // Always enabled: a tap on an incomplete form reveals what's
        // missing (submit errors route to the fields), never a dead button.
        onPress={() => submit()}>
              Check my eligibility
            </OnboardingScreen.PrimaryAction>}
        </form.Subscribe>
      </OnboardingScreen.Footer>
    </OnboardingScreen>;
}